import { Component, EventEmitter, inject, input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import "@customer-web-app/JavaScript/CyberSourceCardPayment.js";
import "@customer-web-app/JavaScript/IFrameMessaging.js";
import { BaseComponent } from '@shared-components/base-component';
import { ComponentHeaderComponent } from '@shared-components/component-header/component-header.component';
import { ModalComponent } from '@shared-components/modal/modal.component';
import { ProductDisplayComponent } from '@shared-components/product-display/product-display.component';
import { FormHelper } from '@shared-helpers/formHelper';
import { UrlHelper } from '@shared-helpers/urlHelper';
import { ExpertModel } from '@shared-model/expert/ExpertModel';
import { ProductModel } from '@shared-model/products/ProductModel';
import { InitialiseManageCardResult } from '@shared-results/payment/InitialiseManageCardResult';
import { TransactionRequestResult } from '@shared-results/payment/TransactionRequestResult';
import { PaymentService } from '@shared-services/payment.service';

@Component({
  selector: 'fourd-manage-card',
  imports: [ModalComponent, ComponentHeaderComponent, ProductDisplayComponent, ReactiveFormsModule],
  templateUrl: './manage-card.component.html',
  host: {
    '[class]': 'chatClass'
  },
  providers: [...BaseComponent.componentProviders],
})
export class ManageCardComponent extends BaseComponent implements OnInit {
  private paymentService = inject(PaymentService);
  protected formHelper = inject(FormHelper);

  readonly expert = input<ExpertModel>(undefined);
  readonly product = input<ProductModel>(undefined);
  readonly products = input<ProductModel[]>(undefined);
  productList: ProductModel[];
  selectedProduct: ProductModel;
  readonly modalDialog = input<boolean>(true);
  chatClass: string = "component-container";

  @Output() close = new EventEmitter<boolean>();

  hasRegisteredCard: boolean;
  currentCardNumberPartial: string;
  
  manageCardLoading: boolean = true;
  submittingData: boolean = false;

  cyberSourceCardPaymentHelper: CyberSourceCardPaymentHelper = null;
  //iFrameMessagingHelper: IFrameMessagingHelper = null;
  paymentExecuting: boolean = false;

  cardForm: FormGroup;

  constructor() { super("ManageCardComponent"); }

  ngOnInit() {
    // Set product list from products input if provided
    this.productList = this.products();
    if (!this.productList) {
      // Product list not set yet, set from proudct input
      this.productList = [this.product()];
    }
    // Set selected product from product input if provided
    this.selectedProduct = this.product();
    if (!this.selectedProduct && this.productList?.length == 1) {
      // Only one item in list, select it
      this.selectedProduct = this.productList[0];
    }

    this.ensureExternalScriptLoaded(
      UrlHelper.externalScriptLoggedInJs,
      (scriptPath: string, event: Event) => {
        // External JS loaded OK, continue with initialisation
        this.cyberSourceCardPaymentHelper = (typeof (CyberSourceCardPaymentHelper) !== 'undefined') ? new CyberSourceCardPaymentHelper() : null;
        this.getManageCardData();
      }, // Success callback
      (scriptPath: string, event: Event) => {
        // Error loading external JS, stop initialisation
        this.constructModalError(
          "Sorry, there was an error initialising payment. Please try again.",
          () => { this.close.emit(false); }
        );
      } // Error callback
    );
   
  }

  initialiseCardForm(manageCardData: InitialiseManageCardResult) {
    this.cardForm = new FormGroup({
      "First Name": new FormControl(manageCardData?.FirstName, [Validators.required, Validators.maxLength(60)]),
      "Last Name": new FormControl(manageCardData?.LastName, [Validators.required, Validators.maxLength(60)]),
      "Street": new FormControl(manageCardData?.Street, [Validators.required/*, Validators.maxLength(60)*/]),
      "City": new FormControl(manageCardData?.City, [Validators.required, Validators.maxLength(50)]),
      "Postcode": new FormControl(manageCardData?.Postcode, [Validators.required, Validators.maxLength(10)]),
      "Country": new FormControl(manageCardData?.IsoCountryCodeTwoLetter, [Validators.required]),
      "Card Number": new FormControl('', { validators: [Validators.required, Validators.maxLength(16), Validators.pattern('^[0-9]{16}$')], updateOn: 'change' }),
      "Expiry Month": new FormControl('', [Validators.required]),
      "Expiry Year": new FormControl('', [Validators.required]),
      "Card Security Code": new FormControl('', [Validators.required, Validators.maxLength(3), Validators.pattern('^[0-9]{3}$')]),
    },
      { updateOn: 'blur' }
    );
    this.formHelper?.initialise(this.cardForm, this.subscriptionTracker);

    // Wire up existing functionality to set card icon
    this.subscriptionTracker.add(this.cardForm.controls["Card Number"].valueChanges.subscribe((event) =>
      this.cyberSourceCardPaymentHelper?.validateCreditCardNumber("ddlCreditCardType", "txtCardAccountNumber"))
    );
  }

  requestPaymentV2(event: Event): void {
    this.handleClickEvent(event);
    this.formHelper?.calculateValidationSummary(true);
    if (this.cardForm?.valid) {
      this.cyberSourceCardPaymentHelper.onCreditCardPaymentButtonClicked();
    }
  }

  getManageCardData(): void {
    this.manageCardLoading = true;

    this.paymentService?.initialiseManageCard((result) => {
      if (result) {
        this.hasRegisteredCard = result.HasRegisteredCard;
        this.currentCardNumberPartial = result.CurrentCardNumberPartial;
        if (this.currentCardNumberPartial?.length > 4) {
          this.currentCardNumberPartial = this.currentCardNumberPartial.substring(this.currentCardNumberPartial.length - 4);
        }
        this.initialiseCardForm(result);
        this.initialiseCyberSourceCardPaymentHelper();
        this.manageCardLoading = false;
      }
      else {
        this.constructModalError(
          "Sorry, an error occured initialising the payment. Please try again.",
          () => { this.close.emit(false); }
        );
      }
    });    
    
  }

  initialiseCyberSourceCardPaymentHelper(): void {
    if (!this.cyberSourceCardPaymentHelper) {
      this.errorService?.logError("InitialiseCyberSourceCardPayment failed because CyberSourceCardPayment not defined", "initialiseCyberSourceCardPaymentHelper","initialiseCyberSourceCardPaymentHelper", false);

      this.constructModalError(
        "Sorry, an error occured initialising the payment. Please try again.",
        () => { this.close.emit(false); }
      );
    }

    if (!this.cyberSourceCardPaymentHelper.enabled) {
      let hasRegisteredCard: boolean = this.hasRegisteredCard;
      let iFramePaymentResultPage: string = UrlHelper.iFramePaymentResult;
      let registerCardUrl: string = this.moduleConfig?.UrlRegisterCardAndPay;
      let commonCallbacksOptions = {
        paymentTypeDescription: "Card"
      };
      let componentInstance: ManageCardComponent = this;

      this.cyberSourceCardPaymentHelper.initialise({
        config: {
          hasRegisteredCard: hasRegisteredCard,
          returnUrl: (location.protocol + "//" + location.host + (this.product()?.ProductId ? "/IFrameAccount/PaymentResult/" : "/IFrameAccount/AuthoriseResult/")),
          //returnUrl: $("#FourD_Data_ReturnUrl").val(),
          isEmbedded: true,
          //paymentFormId: paymentFormId,
          requestFieldsUrl: '/CyberSourceSop/RequestPaymentFields',
          registeredCardRequired: false,
        },
        callbacks: Object.assign({},
          this.createCommonCallbacks(commonCallbacksOptions),
          {
            onRequestFieldsFetched: function (responseObject: any, productData: any) {
              let cyberSourceCardPaymentHelper = componentInstance?.cyberSourceCardPaymentHelper;
              let showError = false;

              if (responseObject && responseObject.RequestFields && cyberSourceCardPaymentHelper) {
                // Fill in form fields from the page
                cyberSourceCardPaymentHelper?.validateCreditCardNumber("ddlCreditCardType", "txtCardAccountNumber"); //Ensure card type is set before values copied, needed for some browser autofill functionality

                if (cyberSourceCardPaymentHelper?.isEmbedded) {
                  // Request fields returned from server, this includes empty placeholders for form fields
                  // Add values from form to request fields
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "card_cardType", "ddlCardCardType");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "card_accountNumber", "txtCardAccountNumber");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "card_cvNumber", "txtCardCvNumber");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "card_expirationMonth", "ddlCardExpirationMonth");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "card_expirationYear", "ddlCardExpirationYear");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "billTo_firstName", "txtBillToFirstName");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "billTo_lastName", "txtBillToLastName");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "billTo_street1", "txtBillToStreet1");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "billTo_city", "txtBillToCity");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "billTo_postalCode", "txtBillToPostalCode");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "billTo_country", "ddlBillToCountry");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "billTo_state", "ddlBillToState");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "card_type", "ddlCreditCardType");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "card_number", "txtCardAccountNumber");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "card_cvn", "txtCardCvNumber");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "bill_to_forename", "txtBillToFirstName");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "bill_to_surname", "txtBillToLastName");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "bill_to_address_line1", "txtBillToStreet1");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "bill_to_address_city", "txtBillToCity");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "bill_to_address_postal_code", "txtBillToPostalCode");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "bill_to_address_country", "ddlBillToCountry");
                  cyberSourceCardPaymentHelper?.addControlValueToRequestFields(responseObject.RequestFields, "bill_to_address_state", "ddlBillToState");
                  cyberSourceCardPaymentHelper?.addExpiryDateControlValueToRequestFields(responseObject.RequestFields, "card_expiry_date", "ddlCardExpirationMonth", "ddlCardExpirationYear");
                  // Submit request fields to iframe
                  cyberSourceCardPaymentHelper?.submitRequestFieldsResponseIFrame(responseObject, cyberSourceCardPaymentHelper?.iFrameElementId, cyberSourceCardPaymentHelper?.iFrameContentUrl);
                }
                else {
                  // Add request fields returned from server, this includes empty placeholders for form fields
                  componentInstance?.errorService?.logError("onRequestFieldsFetched failed because isEmbedded not true", "onRequestFieldsFetched", responseObject, false);
                  showError = true;
                }
              }
              else {
                componentInstance?.errorService?.logError("onRequestFieldsFetched failed because some data not defined", "onRequestFieldsFetched", responseObject, false);
                showError = true;
              }

              if (showError) {
                componentInstance?.constructModalError(
                  "Sorry, an error occured submitting the payment. Please try again.",
                  () => { componentInstance?.close.emit(false); }
                );
              }
            },
          })
      });
    }
  }

  createCommonCallbacks(options: any) {

  if (!options) options = {};

  // Return callbacks array
  return {
    onProductDataRequested: () => { return this.getSelectedProductData(); },
    onError: (err: any, stageText: any, wasPaymentExecuting: any, wasProductMissing: any) => {

      this.errorService?.logError(`'${err}', error: '${stageText}'`, `Angular ManageCardComponent, wasPaymentExecuting: ${wasPaymentExecuting}, wasProductMissing: ${wasProductMissing}`, err, true);

      if (typeof err === 'string' && err.includes('No response payload received')) {
        this.submittingData = false;
        this.paymentExecuting = false;
        return;
      }

      if (wasPaymentExecuting) {
        this.constructModalError(
          "Sorry, an unexpected error occured. Please try again.",
          () => { this.close.emit(false); }
        );

        this.submittingData = false;
        this.paymentExecuting = false;
      }
    },
    onPaymentExecuting: () => {
     //console.log("onPaymentExecuting");
      this.submittingData = true;
      this.paymentExecuting = true;
    },
    onPaymentCancelled: () => {
     // console.log("onPaymentCancelled");
      this.submittingData = false;
      this.paymentExecuting = false;
    },
    onPaymentExecuted: (isAccepted: boolean, responseData: TransactionRequestResult) => {
      //console.log("onPaymentExecuted:");// + JSON.stringify(responseData));
      this.submittingData = false;
      this.paymentExecuting = false;

      if (isAccepted) {
        // TODO - update TransactionRequestResult to include latest member data so that refresh not needed here
        this.subscriptionTracker?.add(this.memberService.refreshMember());
        this.constructModalInfo(
          `Successful ${options?.paymentTypeDescription ? options?.paymentTypeDescription + " " : ""}payment of ${responseData?.TransactionAmount?.AmountFormatted}`,
          () => { this.close.emit(true); }
        );
      }
      else {
        this.constructModalError(
          "Sorry, something went wrong. We've introduced extra security which may have caused your transaction to decline. Please try again.",
          () => { this.close.emit(false); }
        );
      }
    }
  };
  }

  getSelectedProductData() {
    const product = this.product();
    const productData = {
      productId: product?.ProductId,
      amountToPay: product?.Price?.Amount,
      offerId: product?.OfferId
    };
    return productData;
  }

  onClose(): void {
    this.close.emit(false);
  }

  onCloseIframe(event: Event): void {
    this.handleClickEvent(event);
    this.close.emit(false);
  }

}
